import React, { Suspense, useEffect, useMemo } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { Spin } from 'antd'
import { useAuthStore } from './store/authStore'
import { loadDecayConfig } from './config/decayConfig'
import { loadGradeConfig } from './config/gradeConfig'
import { loadFwdRatingConfig } from './config/fwdRatingConfig'
import type { RouteConfig } from './types/route'
import { staticRoutes } from './router'
import TechLayout from './components/layout/TechLayout'
import GlobalImagePreview from './components/common/GlobalImagePreview'

/** 路由保护组件 */
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { token } = useAuthStore()
  const location = useLocation()

  if (!token) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }
  return <>{children}</>
}

/** 加载中组件 */
const PageLoading: React.FC = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <Spin size="large" />
  </div>
)

/**
 * 递归渲染路由
 * @param routes 路由配置数组
 * @param parentPath 父路径（用于判断是否为一级路由）
 */
const renderRoutes = (routes: RouteConfig[], parentPath = ''): React.ReactNode => {
  return routes.map((route, index) => {
    const fullPath = parentPath + route.path
    const key = fullPath + index

    // 处理重定向路由
    if (route.redirect) {
      return <Route key={key} path={route.path} element={<Navigate to={route.redirect} replace />} />
    }

    // 判断是否为一级路由（没有父路径）
    const isTopLevelRoute = !parentPath
    // 判断是否需要 TechLayout：一级路由 + 排除登录页和404
    const needsLayout = isTopLevelRoute && route.path !== '/login' && route.path !== '*'

    // 处理 404 路由
    if (route.path === '*') {
      const element = route.authRequired !== false ? (
        <ProtectedRoute>
          <route.component />
        </ProtectedRoute>
      ) : (
        <route.component />
      )
      return <Route key={key} path="*" element={element} />
    }

    // 处理有子路由的路由
    if (route.children) {
      // 一级路由用 TechLayout 包裹，子路由不再包裹
      const LayoutWrapper = needsLayout ? TechLayout : React.Fragment
      const element = route.authRequired !== false ? (
        <ProtectedRoute>
          <LayoutWrapper>
            <route.component />
          </LayoutWrapper>
        </ProtectedRoute>
      ) : (
        <LayoutWrapper>
          <route.component />
        </LayoutWrapper>
      )
      return (
        <Route key={key} path={route.path} element={element}>
          {route.children.length > 0 && renderRoutes(route.children, route.path)}
        </Route>
      )
    }

    // 普通叶子路由 - 只有一级路由需要 TechLayout
    const component = needsLayout ? <TechLayout><route.component /></TechLayout> : <route.component />
    const element = route.authRequired !== false ? (
      <ProtectedRoute>{component}</ProtectedRoute>
    ) : (
      <route.component />
    )

    return <Route key={key} path={route.path} element={element} />
  })
}

const App: React.FC = () => {
  const location = useLocation()
  const { dynamicRoutes, token, fetchMenus, isHydrated, isRouteBootstrapDone } = useAuthStore()
  const shouldBootstrapProtectedRoutes = Boolean(token) && location.pathname !== '/login'

  // 应用启动或重新登录后拉取一次菜单；刷新时优先使用本地持久化菜单恢复路由。
  useEffect(() => {
    if (isHydrated && shouldBootstrapProtectedRoutes) {
      // console.log('[App] Fetching menus after hydration')
      void fetchMenus().catch(() => undefined)
      // 顺带拉取全站衰变/合成常量与分级阈值（权威源在后端）；失败时静默沿用默认值，不阻塞。
      void loadDecayConfig()
      void loadGradeConfig()
      void loadFwdRatingConfig()
    }
  }, [fetchMenus, isHydrated, shouldBootstrapProtectedRoutes])

  // 使用 useMemo 获取合并后的路由，依赖 dynamicRoutes
  const routes = useMemo(() => {
    const nonStatic404 = staticRoutes.filter(r => r.path !== '*')
    const static404 = staticRoutes.find(r => r.path === '*')
    return [...nonStatic404, ...dynamicRoutes, ...(static404 ? [static404] : [])]
  }, [dynamicRoutes])

  if (!isHydrated || (shouldBootstrapProtectedRoutes && !isRouteBootstrapDone)) {
    return <PageLoading />
  }

  // console.log('[App] Current routes:', routes)

  return (
    <Suspense fallback={<PageLoading />}>
      <>
        <Routes>
          {renderRoutes(routes)}
        </Routes>
        <GlobalImagePreview />
      </>
    </Suspense>
  )
}

export default App
